"use client";

import React from 'react';
import styles from './WarrantyAgreement.module.css';

interface WarrantyAgreementProps {
  enableVideo?: boolean;
  backgroundVideoMp4?: string;
  backgroundVideoWebm?: string;
  videoPoster?: string;
}

export default function WarrantyAgreement({ 
  enableVideo = true, 
  backgroundVideoMp4 = '/video/rental_fog.mp4', 
  backgroundVideoWebm = '/video/fog-creeping.webm', 
  videoPoster = '/img/fog_rental.png' 
}: WarrantyAgreementProps) {
  return (
    <div className={styles.container}>
      {/* Background Video */}
      {enableVideo && (
        <div className={styles.background}>
          <video
            className={styles.video}
            autoPlay
            muted
            loop
            playsInline
            poster={videoPoster}
          >
            <source src={backgroundVideoMp4} type="video/mp4" />
            <source src={backgroundVideoWebm} type="video/webm" />
          </video>
          <div className={styles.overlay} />
        </div>
      )}
      
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <img
            loading="lazy"
            src="https://cdn.builder.io/api/v1/image/assets/TEMP/03b529fd8f1563ec8c5630d4ee62b758b4f401a3b4ef42fa7d4e3500fcf4fab6?apiKey=7d740121674b4225a077dc91176b5085&"
            className={styles.logo}
            alt="NeutraFog logo"
          />
          <div className={styles.badge}>Legal Agreement</div>
          <h1 className={styles.title}>Warranty Agreement</h1>
          <p className={styles.subtitle}>
            Comprehensive warranty coverage for your NeutraFog equipment.
          </p>
        </div>

        <div className={styles.content}>
          {/* Covered Product */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Covered Product</h2>
            <div className={styles.productInfo}>
              <strong>NeutraFog Fogging Equipment</strong>
              <div className={styles.infoGrid}>
                <span className={styles.infoLabel}>Model Number:</span>
                <span className={styles.infoValue}>MODEL -</span>
                <span className={styles.infoLabel}>Buyer Name:</span>
                <span className={styles.infoValue}>_____________________</span>
                <span className={styles.infoLabel}>Buyer Address:</span>
                <span className={styles.infoValue}>_____________________</span>
              </div>
            </div>
          </section>

          {/* Warranty Coverage */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Warranty Coverage</h2>
            <div className={styles.sectionContent}>
              NeutraFog LLC warrants the Covered Product to be free of all defects in material and workmanship for <strong>1 year from March 01, 2021</strong>. This warranty extends to the original buyer only and each successive buyer within the warranty period.
            </div>
            <div className={styles.highlight}>
              <strong>Coverage Details:</strong> Within the period of this warranty, NeutraFog LLC will repair or replace, free of charge, any part proving defective in material or workmanship. All warranty repairs and service must be performed by an authorized NeutraFog LLC technician, or at an authorized NeutraFog LLC service facility.
            </div>
            <div className={styles.sectionContent}>
              All expenses related to replacing or repairing a defective part under this warranty shall be assumed by NeutraFog LLC except for <strong>shipping costs</strong>, which shall be assumed by the buyer.
            </div>
          </section>

          {/* Limitation of Damages */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Limitation of Damages</h2>
            <div className={styles.warning}>
              <div className={styles.warningTitle}>Important Legal Notice</div>
              In no event shall NeutraFog LLC be liable for consequential damages for breach of this warranty. Some States do not allow the exclusion or limitation of incidental or consequential damages, so the above limitation or exclusion may not apply to the buyer.
            </div>
          </section>

          {/* Warranty Exclusions */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Warranty Exclusions</h2>
            <div className={styles.sectionContent}>
              This warranty does not apply to any costs, repairs, or services for the following:
            </div>
            <ol className={styles.numberedList}>
              <li className={styles.numberedItem}>
                Service calls to correct the installation of the Covered Product, or to explain the usage of the product to the buyer.
              </li>
              <li className={styles.numberedItem}>
                Repairs necessitated by use other than normal use.
              </li>
              <li className={styles.numberedItem}>
                Damage resulting from misuse, abuse, accidents, alterations, or improper installation.
              </li>
              <li className={styles.numberedItem}>
                Corrective work necessitated by repairs made by anyone other than a NeutraFog LLC authorized service technician.
              </li>
            </ol>
          </section>

          {/* How to Obtain Warranty Service */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>How to Obtain Warranty Service</h2>
            
            <div className={styles.sectionContent}>
              <strong>For warranty repairs at an authorized service center:</strong>
            </div>
            <div className={styles.sectionContent}>
              The buyer should carefully pack the Covered Product, preferably in the original packing materials, and deliver it, together with a copy of the original purchase receipt and a description of the problem, to our repair and service facility:
            </div>
            
            <div className={styles.address}>
              15300 Valley View Avenue, Unit E14<br/>
              La Mirada, California 90638
            </div>
            
            <div className={styles.exclusion}>
              <div className={styles.exclusionTitle}>Shipping Notice</div>
              If the buyer sends the product by U.S. mail, we recommend that the buyer insure it and send it return receipt requested. We accept no liability for products lost or misplaced in shipment.
            </div>

            <div className={styles.sectionContent}>
              <strong>Alternative warranty service options:</strong>
            </div>
            <div className={styles.sectionContent}>
              Upon discovery of any defect, malfunction, or nonconformity in the Covered Product, the buyer may choose one of the following options:
            </div>
            
            <ul className={styles.list}>
              <li className={styles.listItem}>
                Pack and deliver the product to our facility with purchase receipt and problem description
              </li>
              <li className={styles.listItem}>
                Return the product to the retail seller or any retail seller of like goods who will:
                <ul className={styles.list} style={{ marginTop: '0.5rem', marginLeft: '1rem' }}>
                  <li className={styles.listItem}>Service or repair the product to conform to warranty</li>
                  <li className={styles.listItem}>Direct you to a reasonably close independent repair facility</li>
                  <li className={styles.listItem}>Replace the product with identical or equivalent goods</li>
                  <li className={styles.listItem}>Refund the original purchase price less amount attributable to prior use</li>
                </ul>
              </li>
            </ul>
          </section>

          {/* Customer Details */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Customer Details</h2>
            <div className={styles.sectionContent}>
              This will facilitate any warranty repairs or service if needed. It is not required that the buyer send in this registration card for this warranty to be effective.
            </div>
          </section>

          {/* Notice to Buyer */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Notice to Buyer</h2>
            <div className={styles.highlight}>
              This warranty gives you specific legal rights, and you may also have other rights that vary from state to state. You have the right to bring any action at law or equity to resolve disputes concerning or to enforce the provisions of this warranty.
            </div>
          </section>

          {/* Contact Section */}
          <section className={styles.contactSection}>
            <h3 className={styles.contactTitle}>Need Warranty Service?</h3>
            <p className={styles.contactText}>
              Contact NeutraFog LLC for all warranty-related questions and service requests.
            </p>
            <div className={styles.address} style={{ marginBottom: '1.5rem' }}>
              NeutraFog LLC<br/>
              15300 Valley View Avenue<br/>
              La Mirada, California 90638
            </div>
            <a 
              href="mailto:support@neutrafog.com?subject=Warranty Service Request" 
              className={styles.contactButton}
            >
              Contact Warranty Service
            </a>
          </section>
        </div>
      </div>
    </div>
  );
}

